import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import calendarNoCalendars from '../../../assets/images/calendarNoCalendars.svg';
import {IntegrationIconsEnum} from '../../common/integrationIcon';
import {CellsEmptySetupActionsButton} from '../../inboxes/cells/content/cellsEmpty/cellsEmptySetupActionsButton';

/*
 * Constants.
 */

const defaultConnectPrompt = (
  <Trans id="nocalendars-connect-viewcalendars">
    Connect your work email account to view your calendars here.
  </Trans>
);

const defaultRenderReauthPrompt = (channelName: string) => (
  <Trans id="nocalendars-reauth-viewcalendars">
    Add calendar permissions to your {channelName} inbox to view your calendars.
  </Trans>
);

/*
 * Props.
 */

interface CalendarsEmptyProps {
  reAuthChannel: ChannelModel | undefined;
  onGoToReAuthChannelSettingsClick: () => void;
  onAddGoogleChannelClick: () => void;
  onAddOffice365ChannelClick: () => void;
  connectPrompt?: ReactNode;
  renderReauthPrompt?: RendererOf<string>;
}

/*
 * Styles.
 */

const StyledEmptyDiv = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px 0px;
  ${center()}
`;

const StyledMiddleDiv = styled.div`
  max-width: 245px;

  display: grid;
  justify-items: center;
  grid-template-rows: auto 24px auto 6px auto 24px auto;
  grid-template-areas:
    'image'
    '.'
    'title'
    '.'
    'prompt'
    '.'
    'button';
`;

const StyledImageDiv = styled.img`
  grid-area: image;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;

  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  text-align: center;
`;

const StyledPromptDiv = styled.div`
  grid-area: prompt;
  text-align: center;
`;

const StyledButtonWrapperDiv = styled.div`
  grid-area: button;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

/*
 * Component.
 */

export const CalendarsEmpty: FC<CalendarsEmptyProps> = (props) => {
  const {
    reAuthChannel,
    renderReauthPrompt = defaultRenderReauthPrompt,
    connectPrompt = defaultConnectPrompt,
  } = props;
  const renderContent = () => {
    if (reAuthChannel) {
      return (
        <>
          <StyledPromptDiv>{renderReauthPrompt(reAuthChannel.displayName)}</StyledPromptDiv>
          <StyledButtonWrapperDiv>
            <Button variant={ButtonVariantsEnum.PRIMARY} onClick={props.onGoToReAuthChannelSettingsClick}>
              <Trans id="nocalendars-reauth-settings">Go to Settings</Trans>
            </Button>
          </StyledButtonWrapperDiv>
        </>
      );
    }

    return (
      <>
        <StyledPromptDiv>{connectPrompt}</StyledPromptDiv>
        <StyledButtonWrapperDiv>
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.PRIMARY}
            title={<Trans id="nocalendars-connect-google">Add with Google</Trans>}
            interactionId="nocalendars-connect-googlebutton"
            integrationIcon={IntegrationIconsEnum.GOOGLE}
            onClick={props.onAddGoogleChannelClick}
          />
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.PRIMARY}
            title={<Trans id="nocalendars-connect-office365">Add with Office365</Trans>}
            interactionId="nocalendars-connect-microsoftbutton"
            integrationIcon={IntegrationIconsEnum.MICROSOFT}
            onClick={props.onAddOffice365ChannelClick}
          />
        </StyledButtonWrapperDiv>
      </>
    );
  };

  return (
    <StyledEmptyDiv>
      <StyledMiddleDiv>
        <StyledImageDiv src={calendarNoCalendars} alt="" />
        <StyledTitleDiv>
          <Trans id="nocalendars">No calendars</Trans>
        </StyledTitleDiv>
        {renderContent()}
      </StyledMiddleDiv>
    </StyledEmptyDiv>
  );
};
